import React from 'react';
import {FormattedMessage} from 'react-intl';
import {RadioButton} from '@/web-components';
import {groupKey, isGroupResolved, sourceTakenBy} from './resolution_state';

const sourceLabel = source => (source.detail ? `${source.name} (${source.detail})` : source.name);

/**
 * An element id from a group key, for aria-labelledby. Every character outside [A-Za-z0-9-] is escaped by its code,
 * `_` included, so two keys that differ only in punctuation never share an id.
 */
const idFor = (testIdPrefix, key, suffix) =>
	`${testIdPrefix}-${String(key).replace(/[^A-Za-z0-9-]/g, char => `_${char.charCodeAt(0).toString(16)}_`)}-${suffix}`;

/**
 * A row that cannot be chosen - a source already taken by another client in this session - with the reason. A label,
 * so the row's text names its radio.
 */
const DisabledOption = ({label, reason}) => (
	<label className="integration-match-option integration-match-option--disabled">
		<RadioButton checked={false} disabled={true} />
		<span className="integration-match-option-label">{label}</span>
		<span className="integration-match-option-reason">{reason}</span>
	</label>
);

/** A label, so the row's text names its radio and a click anywhere on the row selects it. */
const Option = ({label, checked, onClick, cy}) => (
	<label className={`integration-match-option${checked ? ' integration-match-option--checked' : ''}`}>
		<RadioButton checked={checked} onClick={onClick} cy={cy} />
		<span className="integration-match-option-label">{label}</span>
	</label>
);

const Title = ({group, messages}) => {
	const title =
		group.kind === 'MANY_CLIENTS' ? (
			<FormattedMessage
				id={messages.manyClientsTitle}
				values={{count: group.clients.length, contact: sourceLabel(group.sources[0])}}
			/>
		) : group.kind === 'MANY_SOURCES' ? (
			<FormattedMessage
				id={messages.manySourcesTitle}
				values={{count: group.sources.length, client: group.clients[0].name}}
			/>
		) : (
			<FormattedMessage
				id={messages.manyToManyTitle}
				values={{clientCount: group.clients.length, contactCount: group.sources.length}}
			/>
		);
	return group.context ? (
		<>
			{title} ({group.context})
		</>
	) : (
		title
	);
};

const GroupHeader = ({group, resolved, messages, titleId}) => (
	<div className="integration-match-group-header">
		<div className="integration-match-group-title" id={titleId}>
			<Title group={group} messages={messages} />
		</div>
		<div className={`integration-match-badge${resolved ? ' integration-match-badge--resolved' : ''}`}>
			<FormattedMessage id={resolved ? messages.resolved : messages.needsReview} />
		</div>
		<div className="integration-match-group-email">{group.email}</div>
	</div>
);

/**
 * One ambiguity, in whichever of the three shapes the integration produced (T4-144; T4-135's card, made
 * provider-neutral). The copy comes from `provider.messages`, the test ids from `provider.testIdPrefix`.
 *
 * Every card can be declined, so a pick can always be undone in place (review round 3, Amar's gap 3): a one-to-many
 * card ends with "do not link" its one source (`messages.doNotLinkSource`), a many-to-one card with "do not link" its
 * one client (`messages.doNotLink`), each storing `null`. T4-135's card has neither; T4-165 carries them to Xero.
 */
const MatchGroupCard = ({group, selection, onSelect, provider}) => {
	const {messages, testIdPrefix} = provider;
	const key = groupKey(group);
	const chosen = selection[key] || {};
	const resolved = isGroupResolved(group, selection);
	const titleId = idFor(testIdPrefix, key, 'title');

	if (group.kind === 'MANY_CLIENTS') {
		return (
			<div className="integration-match-group" data-cy={`${testIdPrefix}-group`}>
				<GroupHeader group={group} resolved={resolved} messages={messages} titleId={titleId} />
				<div className="integration-match-group-prompt">
					<FormattedMessage id={messages.manyClientsPrompt} />
				</div>
				<div role="radiogroup" aria-labelledby={titleId}>
					{group.clients.map(client => (
						<Option
							key={client.clientId}
							label={client.name}
							checked={chosen.clientId === client.clientId}
							onClick={() => onSelect(key, {clientId: client.clientId})}
							cy={`${testIdPrefix}-client-${client.clientId}`}
						/>
					))}
					<Option
						label={<FormattedMessage id={messages.doNotLinkSource} />}
						checked={chosen.clientId === null}
						onClick={() => onSelect(key, {clientId: null})}
						cy={`${testIdPrefix}-skip-source-${key}`}
					/>
				</div>
			</div>
		);
	}

	if (group.kind === 'MANY_SOURCES') {
		const client = group.clients[0];

		return (
			<div className="integration-match-group" data-cy={`${testIdPrefix}-group`}>
				<GroupHeader group={group} resolved={resolved} messages={messages} titleId={titleId} />
				<div className="integration-match-group-prompt">
					<FormattedMessage id={messages.manySourcesPrompt} values={{client: client.name}} />
				</div>
				<div role="radiogroup" aria-labelledby={titleId}>
					{group.sources.map(source => (
						<Option
							key={source.id}
							label={sourceLabel(source)}
							checked={chosen.sourceId === source.id}
							onClick={() => onSelect(key, {sourceId: source.id})}
							cy={`${testIdPrefix}-source-${source.id}`}
						/>
					))}
					<Option
						label={<FormattedMessage id={messages.doNotLink} />}
						checked={chosen.sourceId === null}
						onClick={() => onSelect(key, {sourceId: null})}
						cy={`${testIdPrefix}-skip-client-${key}`}
					/>
				</div>
			</div>
		);
	}

	const byClientId = chosen.byClientId || {};
	const choose = (clientId, sourceId) => onSelect(key, {byClientId: {...byClientId, [clientId]: sourceId}});

	return (
		<div className="integration-match-group" data-cy={`${testIdPrefix}-group`}>
			<GroupHeader group={group} resolved={resolved} messages={messages} titleId={titleId} />
			<div className="integration-match-group-prompt">
				<FormattedMessage id={messages.manyToManyPrompt} />
			</div>
			{group.clients.map(client => {
				// One choice per client, so each client's block is its own radio group, named by the client.
				const clientNameId = idFor(testIdPrefix, key, `client-${client.clientId}`);

				return (
					<div className="integration-match-client-block" key={client.clientId}>
						<div className="integration-match-client-name" id={clientNameId}>
							{client.name}
						</div>
						<div role="radiogroup" aria-labelledby={clientNameId}>
							{group.sources.map(source => {
								const takenBy = sourceTakenBy(group, selection, source.id, client.clientId);

								return takenBy ? (
									<DisabledOption
										key={source.id}
										label={sourceLabel(source)}
										reason={<FormattedMessage id={messages.takenBy} values={{client: takenBy}} />}
									/>
								) : (
									<Option
										key={source.id}
										label={sourceLabel(source)}
										checked={byClientId[client.clientId] === source.id}
										onClick={() => choose(client.clientId, source.id)}
										cy={`${testIdPrefix}-pair-${client.clientId}-${source.id}`}
									/>
								);
							})}
							<Option
								label={<FormattedMessage id={messages.doNotLink} />}
								checked={byClientId[client.clientId] === null}
								onClick={() => choose(client.clientId, null)}
								cy={`${testIdPrefix}-skip-${client.clientId}`}
							/>
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default MatchGroupCard;
